import React, { useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { computeRoiInvestissements } from '../../utils/ameliorations';
import { formatMoney } from '../../utils/format';
import { TrendingUp, TrendingDown, Hammer, Hourglass, LineChart as LineChartIcon } from 'lucide-react';

const COULEURS_MARQUEURS = ['#f59e0b', '#38bdf8', '#a78bfa', '#f472b6', '#34d399', '#fb923c', '#facc15', '#22d3ee'];

/** SCD R3+ — Historique & ROI : CA journalier annoté par les achats d'améliorations. */
export const HistoriqueInvestissements = () => {
  const retail = useGameStore(s => s.retail);
  const totalDays = useGameStore(s => s.totalDays) || 0;

  const histo = retail?.stats?.histo || [];
  const roi = useMemo(() => computeRoiInvestissements(retail, totalDays), [retail, totalDays]);
  const roiVisibles = useMemo(
    () => roi.filter(e => histo.length === 0 || (e.jourEffet ?? e.jour) >= (histo[0]?.d || 0)),
    [roi, histo],
  );

  if (roi.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-ink-600 bg-ink-900/30 p-6 text-center" data-testid="historique-invest-vide">
        <LineChartIcon className="w-8 h-8 text-slate-600 mx-auto mb-2" />
        <p className="text-xs text-slate-400 font-bold">Aucun investissement pour le moment</p>
        <p className="text-[11px] text-slate-500 mt-1">Achetez une amélioration dans l'onglet « Améliorations » : son effet sur le CA sera tracé ici, avant/après travaux.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="historique-invest">
      <div className="rounded-lg border border-ink-600 bg-ink-900/40 p-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
          📈 CA journalier magasins (hors web) — chaque trait vertical = mise en service d'une amélioration
        </p>
        {histo.length >= 2 ? (
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={histo} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="d" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(d) => `J${d}`} />
                <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={36} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 11 }}
                  labelFormatter={(d) => `Jour ${d}`}
                  formatter={(v) => [formatMoney(v), 'CA']}
                />
                {roiVisibles.map((e, i) => (
                  <ReferenceLine
                    key={`${e.amelioId}-${e.jour}-${i}`}
                    x={e.jourEffet ?? e.jour}
                    stroke={COULEURS_MARQUEURS[i % COULEURS_MARQUEURS.length]}
                    strokeDasharray="4 3"
                    label={{
                      value: e.nom.length > 14 ? `${e.nom.slice(0, 13)}…` : e.nom,
                      angle: -90, position: 'insideTopRight',
                      fill: COULEURS_MARQUEURS[i % COULEURS_MARQUEURS.length], fontSize: 9,
                    }}
                  />
                ))}
                <Line type="monotone" dataKey="ca" stroke="#34d399" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-[11px] text-slate-500 py-6 text-center">Jouez quelques jours pour voir la courbe de CA se construire.</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="roi-cartes">
        {[...roi].reverse().map((e, i) => (
          <div key={`${e.amelioId}-${e.jour}-${i}`} className="rounded-lg border border-ink-600 bg-ink-900/30 p-2.5" data-testid={`roi-carte-${e.amelioId}-${e.jour}`}>
            <div className="flex items-center gap-1.5">
              <Hammer className="w-3.5 h-3.5 text-accent shrink-0" />
              <span className="font-bold text-xs text-slate-100">{e.nom}</span>
              <span className="ml-auto font-mono text-[10px] text-slate-400 whitespace-nowrap">J{e.jour} · {formatMoney(e.cout)}</span>
            </div>
            <p className="text-[10px] text-slate-500 mt-0.5">{e.magNom}</p>
            {e.enTravaux ? (
              <p className="flex items-center gap-1 text-[10px] text-amber-400 font-bold mt-1.5"><Hourglass className="w-3 h-3" /> Travaux en cours — effet mesuré à partir du jour {e.jourEffet}</p>
            ) : (
              <div className="mt-1.5 grid grid-cols-3 gap-1.5 font-mono text-[10px]">
                <div className="rounded bg-ink-950/60 px-1.5 py-1">
                  <p className="text-slate-500">CA avant (7 j)</p>
                  <p className="text-slate-200 font-bold">{e.avant != null ? formatMoney(e.avant) : '—'}/j</p>
                </div>
                <div className="rounded bg-ink-950/60 px-1.5 py-1">
                  <p className="text-slate-500">CA après (7 j)</p>
                  <p className="text-slate-200 font-bold">{e.apres != null ? formatMoney(e.apres) : '—'}/j</p>
                </div>
                <div className="rounded bg-ink-950/60 px-1.5 py-1">
                  <p className="text-slate-500">Impact</p>
                  {e.delta == null ? (
                    <p className="text-slate-400 font-bold">—</p>
                  ) : e.delta >= 0 ? (
                    <p className="text-emerald-400 font-bold flex items-center gap-0.5"><TrendingUp className="w-3 h-3" /> +{formatMoney(e.delta)}/j</p>
                  ) : (
                    <p className="text-rose-400 font-bold flex items-center gap-0.5"><TrendingDown className="w-3 h-3" /> {formatMoney(e.delta)}/j</p>
                  )}
                </div>
              </div>
            )}
            {!e.enTravaux && e.delta != null && (
              <p className="text-[10px] mt-1.5 text-slate-400">
                {!e.complet && <span className="text-amber-400 font-bold">Mesure partielle (moins de 7 j après travaux) · </span>}
                {e.delta > 0
                  ? <>Amortissement estimé : <span className="text-emerald-400 font-bold">≈ {e.amortJours} jour{e.amortJours > 1 ? 's' : ''}</span> au rythme actuel.</>
                  : <span className="text-slate-500">Pas encore de gain mesurable — l'effet peut être masqué par la saison, la météo commerciale ou d'autres décisions.</span>}
              </p>
            )}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-500 italic">
        💡 Pédagogie : la comparaison avant/après isole mal les effets croisés (promos, saisonnalité, autres achats rapprochés). Croisez avec la décomposition du trafic du Dashboard pour un diagnostic complet.
      </p>
    </div>
  );
};
